import React from "react";
import Zoomable from "./Zoomable";
import useRevealOnScroll from "./useRevealOnScroll";
import { cloudinaryFull, cloudinaryPlate, cloudinaryPlateThumb } from "./cloudinary";

// Only http(s)/mailto links and our OWN paths are ever rendered (defense-in-depth
// on top of the server-side vetting). NEVER javascript:/data: schemes.
//
// The relative case matters: the old dupontfamily.com WordPress site is gone, so
// the pieces that used to link to its appendix and forum now point at our own
// /footprint?tab=history#a-appendix and /connect. Before this, the gate below
// only accepted http(s)/mailto — so those links failed it, Paragraph fell through
// to its no-links branch, and six pieces said "Click here to visit the appendix."
// with nothing to click.
//
// A single leading slash only. "//evil.com" is protocol-relative and points at
// somebody else's site entirely, so it must NOT pass as one of ours.
function isInternalHref(href) {
  return typeof href === "string" && /^\/(?!\/)/.test(href);
}

function isSafeHref(href) {
  if (typeof href !== "string") return false;
  return isInternalHref(href) || /^(https?:|mailto:)/i.test(href);
}

// Our own pages open in this tab; anything off-site opens in a new one. Sending
// an internal link to a new tab would boot up a second copy of the whole app and
// make the reader sign in again, which is the opposite of what these links are for.
function linkTarget(href) {
  return isInternalHref(href)
    ? {}
    : { target: "_blank", rel: "noopener noreferrer" };
}

// A paragraph is plain text with optional inline links ({text, href}). We weave the
// vetted links in as real <a> nodes by splitting the text around each link's text.
// There is deliberately no dangerouslySetInnerHTML anywhere.
function Paragraph({ block }) {
  const links = Array.isArray(block.links)
    ? block.links.filter((l) => l && l.text && isSafeHref(l.href))
    : [];
  const cls =
    "mb-[16px] leading-[1.75] text-[16px] text-gray-800 dark:text-gray-200";
  if (links.length === 0) return <p className={cls}>{block.text}</p>;

  let nodes = [block.text || ""];
  links.forEach((l, li) => {
    nodes = nodes.flatMap((n, ni) => {
      if (typeof n !== "string") return [n];
      const parts = n.split(l.text);
      const out = [];
      parts.forEach((p, pi) => {
        if (p) out.push(p);
        if (pi < parts.length - 1) {
          out.push(
            <a
              key={`${li}-${ni}-${pi}`}
              href={l.href}
              {...linkTarget(l.href)}
              className="text-dupontBlue dark:text-dupontGoldLt underline"
            >
              {l.text}
            </a>
          );
        }
      });
      return out;
    });
  });
  return <p className={cls}>{nodes}</p>;
}


// One place, revealed as you reach it. Pulled out of the switch above because a
// hook cannot be called inside a case; it also keeps the scroll behaviour in one
// readable place.

// One of Blair's three details. Her icons came from Divi's icon font in red
// (#f81b18); these are inline SVGs so nothing extra has to load, in a red
// darkened just enough to hold contrast on both the grey and white bands.
function Tidbit({ tidbit }) {
  const ICONS = {
    website: "M12 2a10 10 0 100 20 10 10 0 000-20zm0 2c1.7 0 3.2 2.6 3.7 6H8.3C8.8 6.6 10.3 4 12 4zM4.3 11h3c.1-2 .5-3.8 1.1-5.2A8 8 0 004.3 11zm0 2a8 8 0 004.4 5.2c-.6-1.4-1-3.2-1.1-5.2h-3zm4 0h7.4c-.5 3.4-2 6-3.7 6s-3.2-2.6-3.7-6zm8.4 0h3a8 8 0 01-4.4 5.2c.6-1.4 1-3.2 1.1-5.2h.3zm0-2h-.3c-.1-2-.5-3.8-1.1-5.2A8 8 0 0119.7 11h-3z",
    address: "M12 2a7 7 0 00-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z",
    phone: "M6.6 10.8a15.1 15.1 0 006.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1A17 17 0 013 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2z",
  };
  const LABEL = { website: "Visit", address: "Find", phone: "Call" };
  const d = ICONS[tidbit.kind] || ICONS.website;

  // Her icons were a bright red (#f81b18). Kept as an accent but darkened —
  // at this size on cream, the original red vibrates and fails contrast.
  const inner = (
    <span className="inline-flex items-center gap-[8px]">
      <svg viewBox="0 0 24 24" aria-hidden="true"
           className="w-[13px] h-[13px] shrink-0 fill-blairRed">
        <path d={d} />
      </svg>
      <span className="text-blairNavy dark:text-gray-300">{tidbit.text}</span>
    </span>
  );

  // Vetted through the SAME gate as every other link on the page. It was not,
  // which quietly made the promise at the top of this file untrue: these URLs
  // were scraped out of WordPress shortcodes, so they are exactly the ones that
  // deserve the check least on trust and most on principle.
  return isSafeHref(tidbit.url) ? (
    <a href={tidbit.url} {...linkTarget(tidbit.url)}
       aria-label={`${LABEL[tidbit.kind] || "Open"}: ${tidbit.text}`}
       className="no-underline border-b border-transparent hover:border-dupontGold/60 transition-colors">
      {inner}
    </a>
  ) : (
    <span>{inner}</span>
  );
}


// Featured attractions: two at a time, changing with the month.
//
// The source page was a flattened Divi layout in which Longwood's write-up
// appeared four times and its heading twice, which is what made this section
// read as choppy. It is now three clean records, of which two are shown.
//
// The pair is chosen from the date, not at random: everyone opening the site in
// the same month sees the same two, it needs no server or stored state, and it
// cannot change under someone mid-read.
function Featured({ places }) {
  const list = Array.isArray(places) ? places.filter(Boolean) : [];
  const [revealRef, shown] = useRevealOnScroll({ inset: 8 });
  if (list.length === 0) return null;

  const now = new Date();
  const monthIndex = now.getFullYear() * 12 + now.getMonth();
  const pick = [];
  for (let k = 0; k < Math.min(2, list.length); k += 1) {
    pick.push(list[(monthIndex + k) % list.length]);
  }
  const monthName = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <section ref={revealRef} className={
      "w-full transition-opacity duration-[900ms] ease-out motion-reduce:transition-none " +
      (shown ? "opacity-100" : "opacity-0")
    }>
      {/* A clean, centred masthead — it was previously left-aligned and ran
          straight into the content with nothing saying what it was. */}
      <header className="text-center pt-[14px] pb-[34px]">
        <div className="font-blairBody font-bold text-[12px] tracking-[0.34em] uppercase text-blairGold">
          Featured this month
        </div>
        <h3 className="font-blairHead text-[22px] sm:text-[30px] tracking-[0.06em] uppercase
                       text-blairNavy dark:text-darkText mt-[13px]">
          Two Places to Visit
        </h3>
        <div className="mx-auto mt-[15px] mb-[13px] h-[3px] w-[52px] bg-blairGold" aria-hidden="true" />
        <p className="font-blairBody text-[13px] text-gray-500 dark:text-gray-400">
          {monthName} · a new pair every month
        </p>
      </header>

      {pick.map((f, fi) => {
        const imgs = (f.images || []).slice(0, 3);
        const tid = [
          f.website && { kind: "website", text: f.website, url: `https://${String(f.website).replace(/^https?:\/\//, "")}` },
          f.address && {
            kind: "address",
            text: f.address,
            url: f.lat && f.lng
              ? `https://www.google.com/maps/dir/?api=1&destination=${f.lat},${f.lng}`
              : "",
          },
          f.phone && { kind: "phone", text: f.phone, url: "" },
        ].filter(Boolean);

        return (
          <div key={fi}
               className={"w-full px-[24px] py-[52px] sm:py-[64px] border-t-[3px] border-white dark:border-neutral-700 " +
                          (fi % 2 === 0 ? "bg-[#efece4] dark:bg-neutral-900" : "bg-[#faf8f3] dark:bg-neutral-800")}>
            <div className="max-w-[1080px] mx-auto">
              {imgs.length > 0 && (
                <div className="grid gap-[2px] mb-[26px]"
                     style={{ gridTemplateColumns: `repeat(${imgs.length}, minmax(0, 1fr))` }}>
                  {imgs.map((src, ii) => (
                    <Zoomable key={ii} images={imgs.map(cloudinaryFull)} index={ii} label={f.name}>
                      <img src={cloudinaryFull(src)} alt={ii === 0 ? f.name : ""} loading="lazy"
                           className="w-full h-[180px] sm:h-[270px] object-cover block" />
                    </Zoomable>
                  ))}
                </div>
              )}
              <div className="max-w-[680px] mx-auto text-center">
                <h4 className="font-blairHead text-[19px] sm:text-[24px] tracking-[0.09em] uppercase
                               text-blairNavy dark:text-darkText">
                  {f.name}
                </h4>
                <div className="mx-auto my-[15px] h-[2px] w-[42px] bg-blairGold" aria-hidden="true" />
                {(f.description || []).map((para, pi) => (
                  <p key={pi} className="mb-[13px] font-blairBody text-[15px] leading-[1.72]
                                         text-[rgba(38,48,73,0.9)] dark:text-gray-300">
                    {para}
                  </p>
                ))}
                {tid.length > 0 && (
                  <div className="mt-[24px] pt-[16px] border-t border-blairNavy/15 flex flex-wrap
                                  justify-center gap-x-[26px] gap-y-[9px] font-blairBody text-[13px]">
                    {tid.map((t, ti) => <Tidbit key={ti} tidbit={t} />)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function Landmark({ block, index = 0 }) {
  const [revealRef, shown] = useRevealOnScroll({ inset: 12 });
  const photos = [block.image, ...(block.photos || [])].filter(Boolean);
  const lead = photos[0];
  const rest = photos.slice(1, 4);
  const cream = block.band === "#d1d1d1";
  // Alternate which side the photograph sits on, so the page has a rhythm
  // instead of ten identical rows. Matches LANDMARKS-PREVIEW.html.
  const flip = index % 2 === 1;

  const tidbits =
    block.tidbits && block.tidbits.length > 0
      ? block.tidbits
      : [
          block.website && { kind: "website", text: block.website, url: block.website },
          block.address && {
            kind: "address",
            text: block.address,
            url:
              block.lat && block.lng
                ? `https://www.google.com/maps/dir/?api=1&destination=${block.lat},${block.lng}`
                : "",
          },
          block.phone && { kind: "phone", text: block.phone, url: "" },
        ].filter(Boolean);

  const plate = lead && (
    <figure className={flip ? "lg:order-2 m-0" : "m-0"}>
      <Zoomable images={[lead, ...rest].map(cloudinaryFull)} index={0} label={block.name}>
        <img
          src={cloudinaryPlate(lead)}
          alt={block.name || ""}
          loading="lazy"
          className="w-full h-[280px] sm:h-[390px] object-cover block"
        />
      </Zoomable>
      {rest.length > 0 && (
        <div
          className="grid gap-[2px] mt-[2px]"
          style={{ gridTemplateColumns: `repeat(${rest.length}, minmax(0, 1fr))` }}
        >
          {rest.map((p2, pi) => (
            <Zoomable key={pi} images={[lead, ...rest].map(cloudinaryFull)} index={pi + 1} label={block.name}>
              <img src={cloudinaryPlateThumb(p2)} alt="" loading="lazy"
                   className="w-full h-[92px] sm:h-[118px] object-cover block" />
            </Zoomable>
          ))}
        </div>
      )}
    </figure>
  );

  return (
    <section
      ref={revealRef}
      id={block.anchor || undefined}
      className={
        // Edge to edge, alternating band, 3px white rule between places —
        // her original, at the preview's scale.
        "relative w-full px-[24px] py-[64px] sm:py-[80px] scroll-mt-[16px] " +
        "border-t-[3px] border-white dark:border-neutral-700 " +
        "transition-opacity duration-[900ms] ease-out motion-reduce:transition-none " +
        (cream ? "bg-[#efece4] dark:bg-neutral-900" : "bg-[#faf8f3] dark:bg-neutral-800") +
        // Fades in ONCE on arrival and stays solid. It must never dim text a
        // reader is in the middle of.
        (shown ? " opacity-100" : " opacity-0")
      }
    >
      <div className="max-w-[1180px] mx-auto grid grid-cols-1 lg:grid-cols-[1.15fr_.85fr] gap-[34px] lg:gap-[52px] items-center">
        {plate}
        <div className={flip ? "lg:order-1" : ""}>
          <div className="font-blairBody font-bold text-[12px] tracking-[0.3em] text-blairGold mb-[13px]">
            {String(index + 1).padStart(2, "0")}
          </div>
          <h3 className="font-blairHead text-[21px] sm:text-[28px] leading-[1.22] tracking-[0.07em]
                         uppercase text-blairNavy dark:text-darkText">
            {block.name}
          </h3>
          <div className="w-[52px] h-[3px] bg-blairGold my-[17px]" aria-hidden="true" />
          {(block.description || []).map((para, pi) => (
            <p key={pi} className="mb-[14px] font-blairBody text-[15px] leading-[1.72]
                                   text-[rgba(38,48,73,0.9)] dark:text-gray-300">
              {para}
            </p>
          ))}
          {tidbits.length > 0 && (
            <div className="mt-[26px] pt-[18px] border-t border-blairNavy/15
                            flex flex-wrap gap-x-[26px] gap-y-[9px] font-blairBody text-[13px]">
              {tidbits.map((t, ti) => <Tidbit key={ti} tidbit={t} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function BlockRenderer({ blocks }) {
  if (!Array.isArray(blocks)) return null;

  // Which landmark is this, counting only landmarks? Used for the 01/02 numeral
  // and to alternate which side the photograph sits on.
  const landmarkOrdinal = new Map();
  let seen = 0;
  blocks.forEach((b, i) => {
    if (b && b.type === "landmark") landmarkOrdinal.set(i, seen++);
  });
  // Only these run edge to edge. Everything else is prose and must sit in a
  // centred column — when the page became full-width, any block without its own
  // max-width started hugging the left margin. Handling it HERE rather than in
  // each case means a new block type cannot quietly reintroduce the problem.
  const FULL_BLEED = new Set(["landmark", "featured"]);

  const render = (block, i) => {
    switch (block && block.type) {
          case "heading": {
            const lvl = Math.min(Math.max(block.level || 2, 2), 3);
            const base =
              "font-semibold text-dupontBlue dark:text-darkText leading-snug";
            return lvl === 2 ? (
              <h2 key={i} className={`text-[22px] mt-[28px] mb-[10px] ${base}`}>
                {block.text}
              </h2>
            ) : (
              <h3 key={i} className={`text-[18px] mt-[22px] mb-[8px] ${base}`}>
                {block.text}
              </h3>
            );
          }
          case "paragraph":
            return <Paragraph key={i} block={block} />;
          case "image":
            return (
              <figure key={i} className="my-[22px]">
                {/* Constrained on purpose. w-full let a small in-body picture --
                    an aside, a joke illustration -- fill the column and tower
                    over the story it belongs to. object-contain also means a
                    low-resolution original is never stretched. */}
                <Zoomable images={[cloudinaryFull(block.src)]} label={block.alt || block.caption}>
                  <img
                    src={cloudinaryFull(block.src)}
                    alt={block.alt || ""}
                    loading="lazy"
                    className="block mx-auto w-full max-h-[400px] object-contain rounded-[6px]"
                  />
                </Zoomable>
                {block.caption && (
                  <figcaption className="text-[13px] text-gray-500 mt-[6px] text-center">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          // Blair's landmark directory from the old site: a photograph, the place's
          // name, its street address, and a short description. She laid these out as
          // a card grid, and flattening them into loose photos threw away the two
          // things that made them useful -- the names and the addresses.
          case "featured":
            return <Featured key={i} places={block.places} />;

          case "landmark":
            return <Landmark key={i} block={block} index={landmarkOrdinal.get(i) ?? 0} />;

          // The teaser grid Blair opened the page with: photo + name, five across.
          case "landmarkIndex":
            return (
              <div
                key={i}
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-[12px] my-[24px]"
              >
                {(block.items || []).map((it, gi) => {
                  // An <a> with no href is not focusable and is not announced as
                  // a link, so a keyboard or screen-reader user meets a tile that
                  // looks interactive and does nothing. Every item has an anchor
                  // today; render the non-linking case as what it actually is.
                  const Tile = it.anchor ? "a" : "div";
                  return (
                  <Tile
                    key={gi}
                    href={it.anchor ? `#${it.anchor}` : undefined}
                    className="group block rounded-[8px] overflow-hidden border border-dupontGoldLt/40
                               bg-white dark:bg-neutral-800 no-underline"
                  >
                    <img
                      src={cloudinaryFull(it.image)}
                      alt={it.name || ""}
                      loading="lazy"
                      className="w-full h-[92px] object-cover group-hover:opacity-90"
                    />
                    <div className="px-[8px] py-[7px] text-[13px] font-semibold text-center
                                    text-dupontBlue dark:text-darkText leading-[1.3]">
                      {it.name}
                    </div>
                  </Tile>
                  );
                })}
              </div>
            );

          case "gallery":
            return (
              <div
                key={i}
                className="grid grid-cols-2 sm:grid-cols-3 gap-[10px] my-[20px]"
              >
                {(block.images || []).map((img, gi) => (
                  <Zoomable key={gi} images={(block.images || []).map((g) => cloudinaryFull(g.src || g))}
                            index={gi} label={img.alt}>
                    <img
                      src={cloudinaryFull(img.src || img)}
                      alt={img.alt || ""}
                      loading="lazy"
                      className="w-full h-[150px] object-cover rounded-[6px]"
                    />
                  </Zoomable>
                ))}
              </div>
            );
          case "quote":
            return (
              <blockquote
                key={i}
                className="border-l-4 border-dupontGold pl-[16px] my-[20px] italic text-gray-700 dark:text-gray-300"
              >
                {block.text}
                {block.attribution && (
                  <footer className="text-[13px] not-italic text-gray-500 mt-[4px]">
                    — {block.attribution}
                  </footer>
                )}
              </blockquote>
            );
          case "linklist":
            return (
              <ul key={i} className="list-disc pl-[20px] my-[16px]">
                {(block.items || [])
                  .filter((it) => it && isSafeHref(it.href))
                  .map((it, li) => (
                    <li key={li}>
                      <a
                        href={it.href}
                        {...linkTarget(it.href)}
                        className="text-dupontBlue dark:text-dupontGoldLt underline"
                      >
                        {it.label || it.href}
                      </a>
                    </li>
                  ))}
              </ul>
            );
          case "divider":
            return <hr key={i} className="my-[28px] border-dupontGoldLt/40" />;
          default:
            return null;
        }
  };

  // Walk the blocks, gathering runs of prose into one centred wrapper and
  // letting full-bleed blocks break out between them.
  const out = [];
  let run = [];
  const flush = () => {
    if (run.length === 0) return;
    out.push(
      <div key={`prose-${out.length}`} className="max-w-[760px] mx-auto px-[4px]">
        {run}
      </div>
    );
    run = [];
  };
  blocks.forEach((block, i) => {
    const node = render(block, i);
    if (node == null) return;
    if (block && FULL_BLEED.has(block.type)) {
      flush();
      out.push(node);
    } else {
      run.push(node);
    }
  });
  flush();

  return <div>{out}</div>;
}

export default BlockRenderer;
